import React, {useContext, useEffect, useMemo, useState} from "react";
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import OrderDashboardFilters from "../dashboard/OrderDashboard/Layout/OrderDashboardFilters.jsx";
import {FilterContext} from "../../components/filters/DashboardFilterContextProvider.jsx";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {ErpCalculationApi} from "../../api/authority/generated/index";
import {useReportingError} from "../../api/utilities/useReportingError.js";
import toast from "react-hot-toast";
import EChart from "./ReportingEChart.jsx";
import {useRenderCurrencyValue, useRenderNumber} from "../../api/utilities/useValueRenderers.js";
import {useTranslation} from "react-i18next";
import {
    ChannelMixColors,
    formatAxisTooltip,
    formatChartNumber,
    formatChartPercent,
    formatCurrencyItemTooltip,
    KpiCard,
    MetricBasis,
    moneyAmount,
    moneyToMajor,
    Panel,
    projectCurrency,
    ReportingPageDescription,
} from "./ReportingLab.jsx";

function discountAmount(discount, metricBasis) {
    return Number(metricBasis === MetricBasis.Completed ? discount?.amountNet : discount?.amountGross) || 0;
}

function discountTurnover(discount, metricBasis) {
    return metricBasis === MetricBasis.Completed ? discount?.turnoverNet : discount?.turnoverGross;
}

function discountName(discount) {
    return discount?.name || discount?.code || "-";
}

export default function ReportingSalesDiscountsLab({project}) {
    const {t} = useTranslation();
    const {since, until, centresAllowListed, domainsAllowListed} = useContext(FilterContext);
    const {getAuthorityConfiguration} = useContext(GlobalContext);
    const renderCurrencyValue = useRenderCurrencyValue();
    const renderNumber = useRenderNumber();
    const [discountData, setDiscountData] = useState(null);
    const [loading, setLoading] = useState(true);
    const {et, retentionExpired} = useReportingError(project.id, setLoading);
    const metricBasis = MetricBasis.Completed;
    const metricBasisMeta = useMemo(() => ({
        [MetricBasis.Marketing]: {
            label: t("reporting_lab.metric_basis.marketing.label", {defaultValue: "Objednávky bez storen"}),
            shortLabel: t("reporting_lab.metric_basis.marketing.short_label", {defaultValue: "Bez storen"}),
            description: t("reporting_lab.metric_basis.marketing.description", {defaultValue: "Všechny objednávky kromě stornovaných, včetně objednávek bez známého stavu."}),
        },
        [MetricBasis.Completed]: {
            label: t("reporting_lab.metric_basis.business.label", {defaultValue: "Obchodní výkon"}),
            shortLabel: t("reporting_lab.metric_basis.business.short_label", {defaultValue: "Dokončené"}),
            description: t("reporting_lab.metric_basis.business.description", {defaultValue: "Jen dokončené objednávky. Vhodné pro majitele, finance a reálný vyfakturovaný výkon."}),
        },
    }), [t]);

    const sinceString = since.format("YYYY-MM-DD");
    const untilString = until.format("YYYY-MM-DD");
    const centreFilterKey = (centresAllowListed ?? []).join("|");
    const domainFilterKey = (domainsAllowListed ?? []).join("|");

    const erpFilter = useMemo(() => ({
        since: sinceString,
        until: untilString,
        selectedCentreIds: centresAllowListed,
        selectedDomainIds: domainsAllowListed,
    }), [sinceString, untilString, centreFilterKey, domainFilterKey]);

    useEffect(() => {
        if (!project?.id) {
            return;
        }

        let active = true;
        setLoading(true);
        setDiscountData(null);

        new ErpCalculationApi(getAuthorityConfiguration())
            .discountPerformance({projectId: project.id, erpFilter})
            .then((nextDiscountData) => {
                if (!active) {
                    return;
                }

                setDiscountData(nextDiscountData);
                setLoading(false);
            })
            .catch((error) => {
                if (!active) {
                    return;
                }

                et(error).then((message) => { if (message) toast.error(message); });
            });

        return () => {
            active = false;
        };
    }, [project?.id, erpFilter]);

    const discounts = useMemo(() => [...(discountData?.discounts ?? [])]
        .filter((discount) => discountAmount(discount, metricBasis) > 0 || moneyAmount(discountTurnover(discount, metricBasis)) > 0)
        .sort((a, b) => discountAmount(b, metricBasis) - discountAmount(a, metricBasis)), [discountData, metricBasis]);
    const discountTurnoverTotal = discounts.reduce((sum, discount) => sum + moneyAmount(discountTurnover(discount, metricBasis)), 0);
    const discountUsageTotal = discounts.reduce((sum, discount) => sum + discountAmount(discount, metricBasis), 0);
    const firstDiscountWithCurrency = discounts.find((discount) => discountTurnover(discount, metricBasis)?.currency);
    const currency = discountTurnover(firstDiscountWithCurrency, metricBasis)?.currency ?? projectCurrency(project);
    const topDiscount = discounts[0];
    const topDiscounts = discounts.slice(0, 10);
    const chartTextStyle = {fontSize: 12, color: "#64748b"};
    const chartLegendStyle = {
        textStyle: {fontSize: 14, color: "#64748b"},
        itemWidth: 16,
        itemHeight: 10,
    };

    const discountUsageOption = {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        grid: {left: 12, right: 18, top: 18, bottom: 28, containLabel: true},
        tooltip: {
            trigger: "axis",
            axisPointer: {type: "shadow"},
            formatter: (params) => formatAxisTooltip(params, formatChartNumber),
        },
        xAxis: {
            type: "value",
            axisLabel: {fontSize: 12, formatter: formatChartNumber},
        },
        yAxis: {
            type: "category",
            data: [...topDiscounts].reverse().map(discountName),
            axisLabel: {fontSize: 12, width: 260, overflow: "truncate"},
        },
        series: [
            {
                name: t("reporting_lab.sales_discounts.usage", {defaultValue: "Použití"}),
                type: "bar",
                data: [...topDiscounts].reverse().map((discount, index) => ({
                    value: discountAmount(discount, metricBasis),
                    itemStyle: {color: ChannelMixColors[(topDiscounts.length - 1 - index) % ChannelMixColors.length]},
                })),
                barMaxWidth: 24,
            },
        ],
    };

    const discountTurnoverOption = {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        tooltip: {
            trigger: "item",
            formatter: (params) => formatCurrencyItemTooltip(params, currency),
        },
        legend: {bottom: 0, left: "center", width: "92%", ...chartLegendStyle},
        series: [
            {
                type: "pie",
                radius: ["44%", "66%"],
                center: ["50%", "44%"],
                avoidLabelOverlap: true,
                label: {
                    show: true,
                    formatter: (params) => `${params.name}: ${formatChartPercent(params.percent)}`,
                    ...chartTextStyle,
                },
                labelLine: {show: true, length: 12, length2: 8},
                data: topDiscounts.slice(0, 8).map((discount) => ({
                    name: discountName(discount),
                    value: moneyToMajor(discountTurnover(discount, metricBasis)),
                })),
            },
        ],
    };

    return <WideSecureLayout periodReport retentionExpired={retentionExpired}
        title={t("reporting_lab.pages.sales_discounts", {defaultValue: "Řádkové slevy"})}
        actions={<OrderDashboardFilters hideFilters={false} showMarketingFilters={false} showModeSwitch={false}/>}
     intro={<ReportingPageDescription
                    className="col-span-12"
                    title={t("reporting_lab.descriptions.sales_discounts.title", {defaultValue: "K čemu slouží slevový přehled"})}
                >
                    {t("reporting_lab.descriptions.sales_discounts.text", {defaultValue: "Ukazuje použití automatických nebo řádkových slev mimo kupony a obrat objednávek, ve kterých se použily; nejde o nominální hodnotu poskytnuté slevy. Objednávka s více slevami se započítá u každé z nich, proto se součty obratu podle slev mohou opakovat."})}
                </ReportingPageDescription>}>
        <div className="grid grid-cols-12 gap-4">

                <div className="col-span-12 md:col-span-4">
                    <KpiCard loading={loading} label={t("reporting_lab.sales_discounts.used_discounts", {defaultValue: "Počet použití slev"})} value={renderNumber(discountUsageTotal)} meta={t("reporting_lab.sales_discounts.unique_discounts", {defaultValue: "Unikátní slevy"}) + `: ${renderNumber(discounts.length)}`}/>
                </div>
                <div className="col-span-12 md:col-span-4">
                    <KpiCard loading={loading} label={t("reporting_lab.sales_discounts.discount_revenue", {defaultValue: "Obrat objednávek se slevou"})} value={renderCurrencyValue({amount: discountTurnoverTotal, currency}, true)} meta={metricBasisMeta[metricBasis]?.shortLabel}/>
                </div>
                <div className="col-span-12 md:col-span-4">
                    <KpiCard loading={loading} label={t("reporting_lab.sales_discounts.top_discount", {defaultValue: "Nejsilnější sleva"})} value={topDiscount ? discountName(topDiscount) : "-"} meta={topDiscount ? `${renderNumber(discountAmount(topDiscount, metricBasis))}x` : "-"}/>
                </div>

                <Panel loading={loading} title={t("reporting_lab.sales_discounts.discount_usage", {defaultValue: "Použití slev"})} className="col-span-12 xl:col-span-7">
                    <EChart option={discountUsageOption} className="h-96"/>
                </Panel>
                <Panel loading={loading} title={t("reporting_lab.sales_discounts.discount_revenue_share", {defaultValue: "Rozpad obratu slev"})} className="col-span-12 xl:col-span-5">
                    <EChart option={discountTurnoverOption} className="h-96"/>
                </Panel>

                <Panel loading={loading} title={t("reporting_lab.sales_discounts.table", {defaultValue: "Přehled slev"})} className="col-span-12">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-100 text-sm dark:divide-slate-700">
                            <thead>
                            <tr className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
                                <th className="py-2 pr-4 font-semibold">{t("reporting_lab.sales_discounts.discount", {defaultValue: "Sleva"})}</th>
                                <th className="py-2 pr-4 font-semibold">{t("reporting_lab.sales_discounts.code", {defaultValue: "Kód"})}</th>
                                <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.sales_discounts.usage", {defaultValue: "Použití"})}</th>
                                <th className="py-2 text-right font-semibold">{t("reporting_lab.table.revenue", {defaultValue: "Obrat"})}</th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                            {discounts.slice(0, 80).map((discount) => <tr key={`${discountName(discount)}-${discount.code}-${discountAmount(discount, metricBasis)}`}>
                                <td className="py-3 pr-4 font-semibold text-slate-900 dark:text-slate-100">{discountName(discount)}</td>
                                <td className="py-3 pr-4 text-slate-600 dark:text-slate-300">{discount.code ?? "-"}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderNumber(discountAmount(discount, metricBasis))}</td>
                                <td className="py-3 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(discountTurnover(discount, metricBasis), true)}</td>
                            </tr>)}
                            </tbody>
                        </table>
                    </div>
                    {discounts.length === 0 && <div className="mt-4 text-sm text-slate-500 dark:text-slate-400">{t("reporting_lab.sales_discounts.empty", {defaultValue: "Pro vybrané období nejsou dostupná data o použitých slevách."})}</div>}
                </Panel>
            </div>
    </WideSecureLayout>;
}
